import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { PageHeader, QueryState } from '../components/page';
import {
  useApprovePrescreeningVersion,
  usePrescreeningQuery,
  usePrescreeningRuns,
} from '../api/hooks';
import type {
  PrescreeningCriterionView,
  PrescreeningQuery,
  PrescreeningRunSummary,
  PrescreeningVersion,
} from '../api/types';
import {
  ConfirmDialog,
  Fact,
  Facts,
  Section,
  dateText,
  practiceErrorText,
} from './practice-agents';
import { criterionChange, describeRule, stageText } from './criteria-query';
import { CriteriaQueryEditor } from './CriteriaQueryEditor';
import {
  DraftBadge,
  QueryStatusBadge,
  SourceCell,
  VersionStatusBadge,
  isUnknownRecord,
  prescreeningLoadError,
  totalsText,
  trialName,
} from './prescreening';

export interface PrescreeningQuerySearch {
  /** Open the editor on this version. */
  edit?: number;
  /** The criterion an operator marked as wrong. */
  criterion?: string;
}

export function validatePrescreeningQuerySearch(
  search: Record<string, unknown>
): PrescreeningQuerySearch {
  const edit = Number(search.edit);
  return {
    edit: Number.isInteger(edit) && edit > 0 ? edit : undefined,
    criterion: typeof search.criterion === 'string' ? search.criterion : undefined,
  };
}

export function PrescreeningQueryRoutePage() {
  const { queryId } = useParams({ from: '/prescreening/queries/$queryId' });
  const search = useSearch({ from: '/prescreening/queries/$queryId' });
  const navigate = useNavigate({ from: '/prescreening/queries/$queryId' });
  return (
    <PrescreeningQueryDetailPage
      queryId={queryId}
      edit={search.edit ? { version: search.edit, criterion: search.criterion } : null}
      onEditClosed={() => void navigate({ search: {}, replace: true })}
    />
  );
}

interface EditTarget {
  version: number;
  criterion?: string;
}

export function PrescreeningQueryDetailPage({
  queryId,
  edit = null,
  onEditClosed,
}: {
  queryId: string;
  edit?: EditTarget | null;
  onEditClosed?: () => void;
}) {
  const query = usePrescreeningQuery(queryId);
  const runs = usePrescreeningRuns({ queryId }, !!query.data);
  const notFound = isUnknownRecord(query.error);
  return (
    <div>
      <Link
        to="/prescreening/queries"
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> Criteria queries
      </Link>
      <QueryState
        isLoading={query.isLoading}
        error={notFound ? null : prescreeningLoadError(query.error)}
      >
        {notFound ? (
          <p role="alert">
            Rudi does not know this criteria query. Return to Criteria queries and pick it again.
          </p>
        ) : query.data ? (
          <QueryView
            query={query.data}
            runs={runs.data ?? []}
            edit={edit}
            onEditClosed={onEditClosed}
          />
        ) : null}
      </QueryState>
    </div>
  );
}

function designText(query: PrescreeningQuery): string {
  const design = query.design;
  if (!design) return 'Not started';
  const outcome = design.outcome ? `, ${design.outcome}` : '';
  return `Round ${design.round}, ${design.status}${outcome}`;
}

function QueryView({
  query,
  runs,
  edit,
  onEditClosed,
}: {
  query: PrescreeningQuery;
  runs: PrescreeningRunSummary[];
  edit: EditTarget | null;
  onEditClosed?: () => void;
}) {
  const approve = useApprovePrescreeningVersion();
  const [editing, setEditing] = useState<EditTarget | null>(edit);
  const [approving, setApproving] = useState<number | null>(null);
  const editable = query.status !== 'rejected';
  const editVersion = editing
    ? query.versions.find(version => version.version === editing.version)
    : undefined;
  // Newest first, each with the version it came after.
  const columns = query.versions
    .map((version, index) => ({ version, previous: query.versions[index - 1] }))
    .reverse();

  const closeEditor = () => {
    setEditing(null);
    onEditClosed?.();
  };

  const confirmApprove = async () => {
    if (approving === null) return;
    try {
      await approve.mutateAsync({ queryId: query.queryId, version: approving });
      toast.success(`Version ${approving} approved`);
      setApproving(null);
    } catch (error) {
      toast.error(practiceErrorText(error, 'Could not approve the version'));
    }
  };

  return (
    <>
      <PageHeader
        title={trialName(query)}
        description={query.title && query.shortTitle ? query.title : query.catalogId}
      />
      <div className="mb-8">
        <Facts label="Criteria query">
          <Fact label="Status">
            <QueryStatusBadge status={query.status} />
          </Fact>
          <Fact label="Active version">
            {query.activeVersion ? `Version ${query.activeVersion}` : 'None yet'}
          </Fact>
          <Fact label="Practice">
            <SourceCell
              source={query.source}
              practiceId={query.practiceId}
              name={query.practiceName}
            />
          </Fact>
          <Fact label="Trial">{query.catalogId}</Fact>
          <Fact label="Designer">{designText(query)}</Fact>
          {query.design?.lastError && (
            <Fact label="Designer error">
              <span className="text-destructive">{query.design.lastError}</span>
            </Fact>
          )}
          <Fact label="Created">{dateText(query.createdAt)}</Fact>
          <Fact label="Updated">{dateText(query.updatedAt)}</Fact>
        </Facts>
      </div>

      <Section
        title="Versions"
        description="Newest first. Changed and New mark what differs from the version before. The practice computer runs drafts too, but the practice sees a shortlist only for the approved version."
      >
        {columns.length === 0 ? (
          <p className="text-sm text-muted-foreground">No versions yet.</p>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-2">
            {columns.map(({ version, previous }) => (
              <VersionColumn
                key={version.version}
                query={query}
                version={version}
                previous={previous}
                runs={runs.filter(run => run.version === version.version)}
                editable={editable}
                approving={approve.isPending}
                onApprove={() => setApproving(version.version)}
                onEdit={criterion => setEditing({ version: version.version, criterion })}
              />
            ))}
          </div>
        )}
      </Section>

      {editing && editVersion && editable && (
        <CriteriaQueryEditor
          query={query}
          version={editVersion}
          criterionId={editing.criterion}
          onClose={closeEditor}
        />
      )}

      <ConfirmDialog
        open={approving !== null}
        title={`Approve version ${approving ?? ''}`}
        description="The practice computer gets it with its next job poll and runs it at once. The practice then sees the shortlist of this version, and the version approved before stops."
        confirmLabel={approve.isPending ? 'Approving…' : 'Approve'}
        pending={approve.isPending}
        onConfirm={() => void confirmApprove()}
        onClose={() => setApproving(null)}
      />
    </>
  );
}

function originText(version: PrescreeningVersion): string {
  if (version.origin === 'designer')
    return version.designRound ? `Designer, round ${version.designRound}` : 'Designer';
  if (version.origin === 'operator') return 'Operator edit';
  return version.origin ?? 'Unknown origin';
}

function VersionColumn({
  query,
  version,
  previous,
  runs,
  editable,
  approving,
  onApprove,
  onEdit,
}: {
  query: PrescreeningQuery;
  version: PrescreeningVersion;
  previous: PrescreeningVersion | undefined;
  runs: PrescreeningRunSummary[];
  editable: boolean;
  approving: boolean;
  onApprove: () => void;
  onEdit: (criterion?: string) => void;
}) {
  const label = `Version ${version.version}`;
  const active = query.activeVersion === version.version;
  const pending = version.status === 'draft' || version.status === 'awaiting_approval';
  const codeSets = Object.entries(version.codeSets ?? {});
  return (
    <article
      aria-label={label}
      className="w-[24rem] shrink-0 space-y-4 rounded-3xl border bg-card p-4 text-sm"
    >
      <header className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-semibold">{label}</h3>
          <VersionStatusBadge status={version.status} />
          {active && <Badge variant="secondary">Active</Badge>}
        </div>
        <p className="text-xs text-muted-foreground">
          {originText(version)}, created {dateText(version.createdAt)}
        </p>
        {version.approvedAt && (
          <p className="text-xs text-muted-foreground">
            Approved {dateText(version.approvedAt)}
            {version.approvedBy ? ` by ${version.approvedBy}` : ''}
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          Candidate threshold {version.candidateThreshold ?? 0.7}
        </p>
      </header>

      {editable && (
        <div className="flex flex-wrap gap-2">
          {pending && (
            <Button
              size="sm"
              disabled={approving}
              aria-label={`Approve version ${version.version}`}
              onClick={onApprove}
            >
              Approve
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            aria-label={`Edit version ${version.version} as new version`}
            onClick={() => onEdit()}
          >
            Edit as new version
          </Button>
        </div>
      )}

      <VersionRuns runs={runs} />

      <div>
        <h4 className="mb-1 text-xs font-medium text-muted-foreground">Code sets</h4>
        {codeSets.length === 0 ? (
          <p className="text-xs text-muted-foreground">None</p>
        ) : (
          <dl className="space-y-0.5 text-xs">
            {codeSets.map(([name, codes]) => (
              <div key={name}>
                <dt className="inline font-medium">{name}</dt>
                <dd className="inline text-muted-foreground"> {codes.join(', ') || 'no codes'}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      <ol aria-label={`Criteria of version ${version.version}`} className="space-y-2">
        {version.criteria.map(criterion => (
          <CriterionItem
            key={criterion.id}
            criterion={criterion}
            version={version}
            change={criterionChange(criterion, previous)}
            editable={editable}
            onMarkWrong={() => onEdit(criterion.id)}
          />
        ))}
      </ol>
    </article>
  );
}

function VersionRuns({ runs }: { runs: PrescreeningRunSummary[] }) {
  const latest = runs[0];
  if (!latest) return <p className="text-xs text-muted-foreground">No run of this version yet.</p>;
  return (
    <div className="rounded-3xl border p-3 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">Latest run</span>
        {latest.draft && <DraftBadge />}
        <Link
          to="/prescreening/runs/$runId"
          params={{ runId: latest.id }}
          className="underline-offset-4 hover:underline"
        >
          {dateText(latest.finishedAt ?? latest.receivedAt)}
        </Link>
      </div>
      <p className="mt-1 text-muted-foreground">{totalsText(latest.totals)}</p>
      {runs.length > 1 && <p className="mt-1 text-muted-foreground">{runs.length} runs in all</p>}
    </div>
  );
}

function CriterionItem({
  criterion,
  version,
  change,
  editable,
  onMarkWrong,
}: {
  criterion: PrescreeningCriterionView;
  version: PrescreeningVersion;
  change: 'new' | 'changed' | null;
  editable: boolean;
  onMarkWrong: () => void;
}) {
  const ruleText =
    !criterion.rule && criterion.stage === 3
      ? "No coded rule. The patient model reads it from the records' text."
      : describeRule(criterion.rule, version.codeSets);
  return (
    <li className="space-y-1 rounded-3xl border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{criterion.id}</span>
        <Badge variant="outline">
          {criterion.kind === 'exclusion' ? 'Exclusion' : 'Inclusion'}
        </Badge>
        {change && <Badge variant="secondary">{change === 'new' ? 'New' : 'Changed'}</Badge>}
        <span className="text-xs text-muted-foreground">{stageText(criterion.stage)}</span>
      </div>
      <p>{criterion.text}</p>
      <p className="text-xs">
        <span className="text-muted-foreground">Rule: </span>
        {ruleText}
      </p>
      {criterion.stage === 3 && (
        <p className="text-xs">
          <span className="text-muted-foreground">Question: </span>
          {criterion.question || 'None yet'}
        </p>
      )}
      {criterion.keywords && criterion.keywords.length > 0 && (
        <p className="text-xs">
          <span className="text-muted-foreground">Keywords: </span>
          {criterion.keywords.join(', ')}
        </p>
      )}
      {criterion.rationale && (
        <p className="text-xs">
          <span className="text-muted-foreground">Rationale: </span>
          {criterion.rationale}
        </p>
      )}
      {editable && (
        <Button
          size="xs"
          variant="ghost"
          aria-label={`Mark ${criterion.id} of version ${version.version} as wrong`}
          onClick={onMarkWrong}
        >
          Mark as wrong
        </Button>
      )}
    </li>
  );
}
